/**
 * PLC-tab extractors and high-level mappers.
 *
 *  - `mapPlantPlcTree(raw)` projects the global PLC navigation tree
 *    (`PLANT_PLC_TREE_CYPHER`) into a flat list of typed
 *    {@link PlantPlcTreeNode}s + the ontology-wide total tag count.
 *  - `mapPlantPlcNodeDetail(raw, nodeId)` projects the single row
 *    returned by `PLANT_PLC_NODE_DETAIL_CYPHER` into the per-kind
 *    discriminated {@link PlantPlcNodeDetail} the right-pane component
 *    switches over.
 *  - The lower-level row extractors (`extractPlcTagOwner`,
 *    `extractPlcScadaTags`, `extractPlcScadaByTagId`,
 *    `extractPlcInfluences`) stay exported so the slice tests can
 *    exercise them directly.
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractNode, extractNodeList, extractRawNode, toIntCount } from '../graph-row'
import { extractComputeRefs } from '../refs'
import { compareByNameThenId } from '../sort'
import { extractPlantEdges } from './edges'
import { extractPlantMeta } from './provenance'
import type {
  CypherReadResult,
  PlantAOI,
  PlantControlPattern,
  PlantController,
  PlantPLCDataType,
  PlantPLCDataTypeMember,
  PlantPLCTag,
  PlantPlcMappedUdt,
  PlantPlcControllerTagsPage,
  PlantPlcNodeDetail,
  PlantPlcNodeKind,
  PlantPlcRobotSignalMapping,
  PlantPlcScadaTag,
  PlantPlcTagDerivation,
  PlantPlcTagInfluence,
  PlantPlcTree,
  PlantPlcTreeNode,
  PlantProgram,
  PlantRobotIOSignal,
  PlantRoutine
} from '../types'

type PlcTagOwnerKind = Exclude<PlantPlcNodeKind, 'PLCTag' | 'PLCDataType' | 'PLCDataTypeMember'>

/**
 * Project the owner of a PLCTag (Controller / Program / Routine / AOI)
 * returned by `PLANT_PLC_NODE_DETAIL_CYPHER`. Returns `null` when the
 * optional match found nothing — a tag with no `HAS_TAG` edge from a
 * container.
 */
export function extractPlcTagOwner(
  value: unknown,
  kindValue: unknown
): { id: string; name: string; kind: PlcTagOwnerKind } | null {
  const owner = extractRawNode(value)
  if (!owner) return null
  if (
    kindValue !== 'Controller' &&
    kindValue !== 'Program' &&
    kindValue !== 'Routine' &&
    kindValue !== 'AOI'
  ) {
    return null
  }
  const id = typeof owner['id'] === 'string' ? owner['id'] : (owner['_id'] as string)
  const name = typeof owner['name'] === 'string' ? owner['name'] : id
  return { id, name, kind: kindValue }
}

function extractMappedUdt(value: unknown): PlantPlcMappedUdt | null {
  const node = extractNode(value)
  return node ? { id: node.id, name: node.name } : null
}

function extractPlcScadaTag(value: unknown): PlantPlcScadaTag | null {
  if (!value || typeof value !== 'object') return null
  const obj = value as Record<string, unknown>
  const base = extractNode<{ id: string; _id: string; name: string; provider_name?: string }>(
    obj['tag']
  )
  if (!base) return null
  const edge = obj['edge']
  if (edge !== 'MAPS_TO_TAG') return null
  const sourceId =
    typeof obj['sourceId'] === 'string'
      ? obj['sourceId']
      : typeof obj['plcTagId'] === 'string'
        ? obj['plcTagId']
        : ''
  const relationship = extractPlantEdges([
    { type: edge, source: sourceId, target: base.id, props: obj['props'] }
  ])[0]
  if (!relationship) return null
  const raw = base as unknown as Record<string, unknown>
  const providerName =
    typeof raw['provider_name'] === 'string' ? (raw['provider_name'] as string) : undefined
  const providerId =
    typeof obj['providerId'] === 'string' ? (obj['providerId'] as string) : undefined
  const hostId = typeof obj['hostId'] === 'string' ? (obj['hostId'] as string) : undefined
  const tagPath = typeof raw['tag_path'] === 'string' ? (raw['tag_path'] as string) : undefined
  const plcTagPath =
    typeof raw['plc_tag_path'] === 'string' ? (raw['plc_tag_path'] as string) : undefined
  const hostUrl = typeof obj['hostUrl'] === 'string' ? (obj['hostUrl'] as string) : undefined
  const project = typeof obj['project'] === 'string' ? (obj['project'] as string) : undefined
  return {
    id: base.id,
    _id: base._id,
    name: base.name,
    edge,
    relationship,
    provider_name: providerName,
    tag_path: tagPath,
    plc_tag_path: plcTagPath,
    provider_id: providerId,
    host_id: hostId,
    host_url: hostUrl,
    project
  }
}

/**
 * Project the `collect(distinct { tag, edge })` shape from
 * `PLANT_PLC_NODE_DETAIL_CYPHER` onto the cross-layer SCADA-tag list
 * surfaced on a PLCTag's detail pane. Sentinel rows (no tag) are
 * filtered out.
 */
export function extractPlcScadaTags(value: unknown): PlantPlcScadaTag[] {
  if (!Array.isArray(value)) return []
  const out: PlantPlcScadaTag[] = []
  for (const item of value) {
    const tag = extractPlcScadaTag(item)
    if (tag) out.push(tag)
  }
  return out
}

/**
 * Project the `plcTagScadaRows` shape (one row per (PLCTag, SCADA-Tag,
 * edge) triple) into the `{ [plcTagId]: PlantPlcScadaTag[] }` lookup
 * the PLCTag table renders against. De-dupes within each PLCTag by
 * `tag.id` so duplicate `MAPS_TO_TAG` rows from the union don't
 * double-up chips.
 */
export function extractPlcScadaByTagId(value: unknown): Record<string, PlantPlcScadaTag[]> {
  const out: Record<string, PlantPlcScadaTag[]> = {}
  if (!Array.isArray(value)) return out
  const seenByTag: Record<string, Set<string>> = {}
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const plcTagId = typeof obj['plcTagId'] === 'string' ? (obj['plcTagId'] as string) : null
    if (!plcTagId) continue
    const tag = extractPlcScadaTag(obj)
    if (!tag) continue
    const dedupeKey = tag.id
    const seen = seenByTag[plcTagId] ?? new Set<string>()
    if (seen.has(dedupeKey)) continue
    seen.add(dedupeKey)
    seenByTag[plcTagId] = seen
    const list = out[plcTagId] ?? []
    list.push(tag)
    out[plcTagId] = list
  }
  return out
}

/** Project one server-paginated controller-tag response. */
export function mapPlantPlcControllerTagsPage(raw: CypherReadResult): PlantPlcControllerTagsPage {
  const row = raw.results[0] ?? {}
  return {
    totalTags: toIntCount(row['totalTags']),
    tags: extractNodeList<PlantPLCTag>(row['tags']).sort(compareByNameThenId),
    scadaByTagId: extractPlcScadaByTagId(row['plcTagScadaRows'])
  }
}

/**
 * Project the `collect(distinct { edge, target, outgoing })` PLCTag
 * influence rows. Each direction (TRIGGERS / GATES / INHIBITS /
 * CONTROLS) surfaces as a row; the panel groups by edge type on the
 * client.
 */
export function extractPlcInfluences(value: unknown): PlantPlcTagInfluence[] {
  if (!Array.isArray(value)) return []
  const out: PlantPlcTagInfluence[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const target = extractNode<PlantPLCTag>(obj['target'])
    if (!target) continue
    const edge = obj['edge']
    if (edge !== 'TRIGGERS' && edge !== 'GATES' && edge !== 'INHIBITS' && edge !== 'CONTROLS') {
      continue
    }
    const outgoing = obj['outgoing'] === true
    out.push({ edge, outgoing, target })
  }
  return out
}

/**
 * Project `CALCULATED_BY` rows shaped `{ tag, viaRoutineUrn, viaRungNumber }`.
 */
export function extractPlcTagDerivations(value: unknown): PlantPlcTagDerivation[] {
  if (!Array.isArray(value)) return []
  const out: PlantPlcTagDerivation[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const tag = extractNode<PlantPLCTag>(obj['tag'])
    if (!tag) continue
    const viaRoutineUrn =
      typeof obj['viaRoutineUrn'] === 'string' && obj['viaRoutineUrn'].length > 0
        ? obj['viaRoutineUrn']
        : undefined
    const viaRungNumber =
      typeof obj['viaRungNumber'] === 'number' && Number.isFinite(obj['viaRungNumber'])
        ? obj['viaRungNumber']
        : undefined
    out.push({ tag, viaRoutineUrn, viaRungNumber })
  }
  return out
}

/**
 * Project `MAPS_TO_ROBOT_SIGNAL` rows shaped
 * `{ signal, method, confidence, register, direction }`.
 */
export function extractPlcRobotSignalMappings(value: unknown): PlantPlcRobotSignalMapping[] {
  if (!Array.isArray(value)) return []
  const out: PlantPlcRobotSignalMapping[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const signal = extractNode<PlantRobotIOSignal>(obj['signal'])
    if (!signal) continue
    const mapping: PlantPlcRobotSignalMapping = { signal }
    if (typeof obj['method'] === 'string' && obj['method'].length > 0) {
      mapping.method = obj['method']
    }
    if (typeof obj['confidence'] === 'number' && Number.isFinite(obj['confidence'])) {
      mapping.confidence = obj['confidence']
    }
    if (typeof obj['register'] === 'string' && obj['register'].length > 0) {
      mapping.register = obj['register']
    }
    if (typeof obj['direction'] === 'string' && obj['direction'].length > 0) {
      mapping.direction = obj['direction']
    }
    out.push(mapping)
  }
  return out
}

/**
 * Project the global PLC navigation tree returned by
 * `PLANT_PLC_TREE_CYPHER`. Each row carries one of the five tree kinds
 * (Controller / Program / Routine / AOI / PLCDataType) plus its parent
 * id + rail badge count; the ontology-wide `totalTagCount` is
 * repeated on every row by the cypher's leading `CALL { ... }` so we
 * pick the last value seen.
 */
export function mapPlantPlcTree(raw: CypherReadResult): PlantPlcTree {
  const nodes: PlantPlcTreeNode[] = []
  let totalTagCount = 0
  for (const row of raw.results) {
    totalTagCount = toIntCount(row['totalTagCount'])
    const node = extractRawNode(row['node'])
    if (!node) continue
    const kind = row['kind']
    if (
      kind !== 'Controller' &&
      kind !== 'Program' &&
      kind !== 'Routine' &&
      kind !== 'AOI' &&
      kind !== 'PLCDataType'
    ) {
      continue
    }
    const id = typeof node['id'] === 'string' ? node['id'] : (node['_id'] as string)
    const name = typeof node['name'] === 'string' ? node['name'] : id
    const parentId = typeof row['parentId'] === 'string' ? row['parentId'] : null
    const routineType =
      typeof row['routineType'] === 'string'
        ? (row['routineType'] as PlantPlcTreeNode['routineType'])
        : undefined
    nodes.push({
      id,
      parentId,
      kind,
      name,
      ...extractPlantMeta(node),
      tagCount: toIntCount(row['tagCount']),
      routineType
    })
  }
  return { nodes, totalTagCount }
}

/**
 * Project the single row returned by `PLANT_PLC_NODE_DETAIL_CYPHER`
 * into the per-kind discriminated {@link PlantPlcNodeDetail}. Throws
 * when the node id wasn't found or the labels include none of the
 * five supported kinds — both signal an upstream data bug, not a
 * recoverable shape variation.
 */
export function mapPlantPlcNodeDetail(raw: CypherReadResult, nodeId: string): PlantPlcNodeDetail {
  const row = raw.results[0]
  if (!row) {
    throw new Error(`PLC node ${nodeId} not found`)
  }
  const labels = Array.isArray(row['labelList']) ? (row['labelList'] as unknown[]) : []
  const strayLabels = Array.isArray(row['strayLabels'])
    ? row['strayLabels'].filter((label): label is string => typeof label === 'string')
    : []
  const isStrayFor = (kind: string): boolean => strayLabels.includes(kind)
  const scadaByTagId = extractPlcScadaByTagId(row['plcTagScadaRows'])
  if (labels.includes('Controller')) {
    const controller = extractNode<PlantController>(row['n'])
    if (!controller) throw new Error(`PLC Controller ${nodeId} could not be projected`)
    return {
      kind: 'Controller',
      isStray: isStrayFor('Controller'),
      controller,
      programs: extractNodeList<PlantProgram>(row['controllerPrograms']).sort(compareByNameThenId),
      aois: extractNodeList<PlantAOI>(row['controllerAois']).sort(compareByNameThenId),
      dataTypes: extractNodeList<PlantPLCDataType>(row['controllerDataTypes']).sort(
        compareByNameThenId
      ),
      patterns: extractNodeList<PlantControlPattern>(row['controllerPatterns']).sort(
        compareByNameThenId
      ),
      tagCount: toIntCount(row['controllerTagCount']),
      mappedComputes: extractComputeRefs(row['controllerMappedComputes'])
    }
  }
  if (labels.includes('Program')) {
    const program = extractNode<PlantProgram>(row['n'])
    if (!program) throw new Error(`PLC Program ${nodeId} could not be projected`)
    return {
      kind: 'Program',
      isStray: isStrayFor('Program'),
      program,
      controller: extractNode<PlantController>(row['programController']),
      routines: extractNodeList<PlantRoutine>(row['programRoutines']).sort(compareByNameThenId),
      tags: extractNodeList<PlantPLCTag>(row['programTags']).sort(compareByNameThenId),
      recursiveTagCount: toIntCount(row['programRecursiveTagCount']),
      scadaByTagId
    }
  }
  if (labels.includes('Routine')) {
    const routine = extractNode<PlantRoutine>(row['n'])
    if (!routine) throw new Error(`PLC Routine ${nodeId} could not be projected`)
    return {
      kind: 'Routine',
      isStray: isStrayFor('Routine'),
      routine,
      program: extractNode<PlantProgram>(row['routineProgram']),
      controller: extractNode<PlantController>(row['routineController']),
      tags: extractNodeList<PlantPLCTag>(row['routineTags']).sort(compareByNameThenId),
      scadaByTagId,
      readsTags: extractNodeList<PlantPLCTag>(row['routineReadsTags']).sort(compareByNameThenId),
      writesTags: extractNodeList<PlantPLCTag>(row['routineWritesTags']).sort(compareByNameThenId)
    }
  }
  if (labels.includes('AOI')) {
    const aoi = extractNode<PlantAOI>(row['n'])
    if (!aoi) throw new Error(`PLC AOI ${nodeId} could not be projected`)
    return {
      kind: 'AOI',
      isStray: isStrayFor('AOI'),
      aoi,
      controller: extractNode<PlantController>(row['aoiController']),
      tags: extractNodeList<PlantPLCTag>(row['aoiTags']).sort(compareByNameThenId),
      instantiatedTagCount: toIntCount(row['aoiInstantiatedTagCount']),
      scadaByTagId
    }
  }
  if (labels.includes('PLCDataType')) {
    const dataType = extractNode<PlantPLCDataType>(row['n'])
    if (!dataType) throw new Error(`PLC PLCDataType ${nodeId} could not be projected`)
    return {
      kind: 'PLCDataType',
      isStray: isStrayFor('PLCDataType'),
      dataType,
      controller: extractNode<PlantController>(row['dataTypeController']),
      members: extractNodeList<PlantPLCDataTypeMember>(row['dataTypeMembers']).sort(
        compareByNameThenId
      ),
      mappedUdt: extractMappedUdt(row['dataTypeMappedUdt']),
      tags: extractNodeList<PlantPLCTag>(row['dataTypeTags']).sort(compareByNameThenId)
    }
  }
  if (labels.includes('PLCDataTypeMember')) {
    const member = extractNode<PlantPLCDataTypeMember>(row['n'])
    if (!member) throw new Error(`PLC PLCDataTypeMember ${nodeId} could not be projected`)
    return {
      kind: 'PLCDataTypeMember',
      isStray: isStrayFor('PLCDataTypeMember'),
      member,
      dataType: extractNode<PlantPLCDataType>(row['memberDataType']),
      controller: extractNode<PlantController>(row['memberController'])
    }
  }
  if (labels.includes('PLCTag')) {
    const tag = extractNode<PlantPLCTag>(row['n'])
    if (!tag) throw new Error(`PLC PLCTag ${nodeId} could not be projected`)
    return {
      kind: 'PLCTag',
      isStray: isStrayFor('PLCTag'),
      tag,
      dataType: extractNode<PlantPLCDataType>(row['tagDataType']),
      owner: extractPlcTagOwner(row['tagOwner'], row['tagOwnerKind']),
      scadaTags: extractPlcScadaTags(row['scadaTagRows']),
      influences: extractPlcInfluences(row['influenceRows']),
      readByRoutines: extractNodeList<PlantRoutine>(row['tagReadByRoutines']).sort(
        compareByNameThenId
      ),
      writtenByRoutines: extractNodeList<PlantRoutine>(row['tagWrittenByRoutines']).sort(
        compareByNameThenId
      ),
      calculatedFrom: extractPlcTagDerivations(row['calculatedFromRows']),
      feeds: extractPlcTagDerivations(row['feedsRows']),
      robotSignals: extractPlcRobotSignalMappings(row['robotSignalRows'])
    }
  }
  throw new Error(`PLC node ${nodeId} has unsupported labels ${labels.join(',')}`)
}
